import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input, Select, Checkbox, Radio } from 'antd';
import { ChevronLeftIcon } from '../../assets/icons';
import {
  LEVELS,
  PLURAL,
  EX,
  OTHER,
  ALL,
  ACT_LABEL,
  MODULES,
  PERM_TOTAL,
  emptyPerms,
  emptyVis,
  typeOf,
  typeChip,
  permCount,
  addRole,
  getRole,
  type Terr,
  type PermMap,
  type VisMap,
} from './clientRolesStore';

/* ------------------------------------------------------------------ *
 * Client Admin — Add a role (separate page). Name, scope, derived role
 * type, and the permissions matrix. `?from=<id>` prefills from an
 * existing role as a template. On save, the role is added and we return
 * to the roles list. Mock (local store).
 * ------------------------------------------------------------------ */

const MATRIX_COLS = 'minmax(200px,2.2fr) repeat(6, minmax(58px,1fr)) minmax(160px,1.4fr)';

export default function ClientRoleFormPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const template = getRole(params.get('from') ?? '');

  const [name, setName] = useState(template ? `${template.name} (copy)` : '');
  const [terr, setTerr] = useState<Terr>(template?.terr ?? 'org');
  const [level, setLevel] = useState(template?.level ?? 'District');
  const [perms, setPerms] = useState<PermMap>(() =>
    template ? (JSON.parse(JSON.stringify(template.perms)) as PermMap) : emptyPerms(),
  );
  const [vis, setVis] = useState<VisMap>(() => (template ? { ...template.vis } : emptyVis()));

  const formType = typeOf(terr);
  const idx = LEVELS.indexOf(level) + 1;
  const below = LEVELS.slice(idx).map((l) => PLURAL[l]);
  const levelHint = below.length
    ? `A user assigned ${EX[level]} also covers its ${below.join(' and ')}.`
    : 'Gram panchayat is the smallest unit; nothing sits below it.';
  const routing =
    formType === 'Functional'
      ? 'Work from every territory can reach this role; what they can do is set by the permissions.'
      : `A request from ${EX[level]} reaches the user assigned to it. One from ${OTHER[level]} does not.`;
  const formPermOn = permCount(perms);

  const togglePerm = (mid: string, a: string) =>
    setPerms((p) => ({ ...p, [mid]: { ...p[mid], [a]: !p[mid][a] } }));
  const setVisFor = (mid: string, v: string) => setVis((vv) => ({ ...vv, [mid]: v }));

  const save = () => {
    if (!name.trim()) return;
    addRole({ name: name.trim(), terr, level, perms, vis });
    navigate('/client/roles');
  };

  return (
    <div className="flex flex-col gap-[18px]">
      <div>
        <button
          type="button"
          onClick={() => navigate('/client/roles')}
          className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-line bg-white pl-2.5 pr-3.5 text-[13px] font-semibold text-ink transition-colors hover:bg-brand-shade"
        >
          <ChevronLeftIcon className="h-3 w-[7px]" />
          All roles
        </button>
      </div>

      <section className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-brand">CLIENT SETUP · ROLES &amp; PERMISSIONS</span>
        <h1 className="text-2xl font-semibold leading-9 tracking-[-0.75px] text-ink">Add a role</h1>
        <p className="max-w-[820px] text-sm leading-6 text-ink-secondary">
          Name the role, choose its scope, and set what it can do in each module. The scope decides the role type and
          whose work reaches it.
        </p>
      </section>

      <div className="flex flex-col gap-4 rounded-xl border border-line/30 bg-white p-5 shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-bold text-ink">Details</h2>
          <span className={['rounded-full px-2.5 py-0.5 text-xs font-semibold', typeChip(formType)].join(' ')}>
            {formType}
          </span>
        </div>

        <label className="flex max-w-[460px] flex-col gap-1">
          <span className="text-xs font-semibold text-ink-secondary">Role name</span>
          <Input size="large" placeholder="e.g. District Coordinator" value={name} onChange={(e) => setName(e.target.value)} />
        </label>

        {/* Scope */}
        <div className="flex flex-col gap-2.5 rounded-lg border border-line/50 p-4">
          <div>
            <h3 className="text-sm font-bold text-ink">Scope</h3>
            <p className="text-[13px] text-ink-secondary">
              Choose whether this role works across the organisation or within territories.
            </p>
          </div>
          <Radio.Group value={terr} onChange={(e) => setTerr(e.target.value)} className="flex flex-col gap-1">
            <Radio value="org" className="!items-start">
              <span className="flex flex-col">
                <span className="text-[13px] font-semibold text-ink">Organisation-wide</span>
                <span className="text-xs text-ink-secondary">Works across the whole state; no territory is assigned</span>
              </span>
            </Radio>
            <Radio value="assigned" className="!items-start">
              <span className="flex flex-col">
                <span className="text-[13px] font-semibold text-ink">Assigned per user</span>
                <span className="text-xs text-ink-secondary">Each user is given one or more territories at the level below</span>
              </span>
            </Radio>
          </Radio.Group>
          {terr === 'assigned' && (
            <div className="flex flex-col gap-2.5 pl-6">
              <label className="flex flex-wrap items-center gap-2.5">
                <span className="text-xs font-semibold text-ink-secondary">Assigned at level</span>
                <Select
                  value={level}
                  onChange={setLevel}
                  style={{ minWidth: 180 }}
                  options={LEVELS.map((l) => ({ value: l, label: l }))}
                />
              </label>
              <ol className="flex flex-wrap items-center gap-1.5" aria-label="Territory hierarchy">
                {['State', ...LEVELS].map((l, i) => {
                  const isSel = i === idx;
                  const isBelow = i > idx;
                  return (
                    <li
                      key={l}
                      className={[
                        'rounded px-2 py-0.5 text-xs font-semibold',
                        isSel
                          ? 'bg-brand text-white'
                          : isBelow
                            ? 'border border-dashed border-brand text-brand'
                            : 'bg-muted-bg text-ink-secondary',
                      ].join(' ')}
                    >
                      {(i ? '› ' : '') + l}
                    </li>
                  );
                })}
              </ol>
              <span className="text-[13px] text-ink-secondary">{levelHint}</span>
            </div>
          )}
          <div className="rounded-md border-l-2 border-brand/40 bg-brand-bg/60 px-3 py-2 text-[13px] text-ink">
            <span className="font-semibold">Routing — </span>
            {routing}
          </div>
        </div>
      </div>

      {/* Permissions */}
      <div className="flex flex-col gap-3 rounded-xl border border-line/30 bg-white p-5 shadow-card">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h3 className="text-lg font-bold text-ink">Permissions</h3>
            <p className="text-[13px] text-ink-secondary">
              Actions apply only to records inside the role's scope. "Records visible" can narrow that further.
            </p>
          </div>
          <span className="text-[13px] text-ink-secondary">
            <span className="font-bold text-ink">{formPermOn}</span> of {PERM_TOTAL} allowed
          </span>
        </div>

        <div className="overflow-x-auto rounded-lg border border-line/50">
          <div className="min-w-[860px]">
            <div
              className="grid items-center bg-[#f9fafb] text-[11px] font-semibold uppercase tracking-[0.03em] text-ink-secondary"
              style={{ gridTemplateColumns: MATRIX_COLS }}
            >
              <div className="px-4 py-3">Module</div>
              {ALL.map((a) => (
                <div key={a} className="px-1 py-3 text-center">
                  {ACT_LABEL[a]}
                </div>
              ))}
              <div className="px-4 py-3">Records visible</div>
            </div>
            {['Operations', 'Administration'].map((g) => (
              <div key={g}>
                <div className="border-t border-line/50 bg-[#fafbfc] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-secondary">
                  {g}
                </div>
                {MODULES.filter((m) => m.group === g).map((m) => (
                  <div
                    key={m.id}
                    className="grid items-center border-t border-[#eef0f3]"
                    style={{ gridTemplateColumns: MATRIX_COLS, minHeight: 58 }}
                  >
                    <div className="px-4 py-2.5">
                      <div className="text-[13px] font-semibold text-ink">{m.name}</div>
                      <div className="text-xs text-ink-secondary">{m.desc}</div>
                    </div>
                    {ALL.map((a) => {
                      const ap = m.acts.indexOf(a) > -1;
                      return (
                        <div key={a} className="flex justify-center">
                          {ap ? (
                            <Checkbox
                              checked={!!perms[m.id][a]}
                              onChange={() => togglePerm(m.id, a)}
                              aria-label={`${m.name}: ${ACT_LABEL[a]}`}
                            />
                          ) : (
                            <span className="text-[#9aa2b1]" aria-hidden>
                              —
                            </span>
                          )}
                        </div>
                      );
                    })}
                    <div className="px-4 py-2">
                      {m.vis ? (
                        <Select
                          size="small"
                          className="w-full"
                          value={vis[m.id]}
                          onChange={(v) => setVisFor(m.id, v)}
                          options={[
                            { value: 'scope', label: 'Within role scope' },
                            { value: 'mine', label: 'Assigned to them only' },
                            { value: 'all', label: 'Entire organisation' },
                          ]}
                        />
                      ) : (
                        <span className="text-[13px] text-ink-secondary">Org-level setting</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button"
            onClick={save}
            disabled={!name.trim()}
            className="h-11 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
          >
            Add role
          </button>
          <button
            type="button"
            onClick={() => navigate('/client/roles')}
            className="h-11 rounded-lg border border-line bg-white px-4 text-[13px] font-semibold text-ink transition-colors hover:bg-brand-shade"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
